import type { CSSProperties } from "react";
import "./AIUsageGears.css";

/** Faithful React rendering of the gear overview in public/ai-30/program/index.html.
 * Keep its SVG geometry, copy, and responsive styles aligned with that page.
 */
const AIUsageGears = () => (
  <div className="home-ai-gears">
    <section className="gear-overview" aria-labelledby="gear-overview-title">
      <div className="gear-overview-head">
        <h2 className="gear-overview-title" id="gear-overview-title">The 4 Gears of AI Usage</h2>
        <p className="gear-overview-lede">USE THE SMALLEST GEAR THAT CAN OWN THE RESULT</p>
      </div>
      <div className="gear-system is-spinning">
          {/* The viewBox and diagonal train fill the teaching stage from the subtitle
               to the Human Intelligence foundation without crowding the outcomes. */}
          <svg className="gear-desktop" viewBox="60 0 880 330" role="img"
               aria-label="Four interlocking AI usage gears resting on a foundation of human intelligence. Chat handles one question or idea. Assistant handles one finished deliverable. Agents handle a multi-step objective. Loops handle a recurring outcome. Each higher gear is smaller and turns faster."
               preserveAspectRatio="xMidYMid meet">
            <defs>
              {/* One reusable twelve-tooth gear. Scaling the same geometry keeps
                   the teeth compatible while the shrinking radii create higher RPM. */}
              <rect id="gear-tooth" x="-7" y="-58" width="14" height="17" rx="2" />
              <g id="gear-form">
                <circle r="46" fill="currentColor" fillOpacity=".85" stroke="currentColor" strokeWidth="2.4" />
                <g fill="currentColor" fillOpacity=".95" stroke="currentColor" strokeWidth="1.4">
                  <use href="#gear-tooth" />
                  <use href="#gear-tooth" transform="rotate(30)" />
                  <use href="#gear-tooth" transform="rotate(60)" />
                  <use href="#gear-tooth" transform="rotate(90)" />
                  <use href="#gear-tooth" transform="rotate(120)" />
                  <use href="#gear-tooth" transform="rotate(150)" />
                  <use href="#gear-tooth" transform="rotate(180)" />
                  <use href="#gear-tooth" transform="rotate(210)" />
                  <use href="#gear-tooth" transform="rotate(240)" />
                  <use href="#gear-tooth" transform="rotate(270)" />
                  <use href="#gear-tooth" transform="rotate(300)" />
                  <use href="#gear-tooth" transform="rotate(330)" />
                </g>
                <circle r="19" fill="#fff" stroke="currentColor" strokeWidth="2" />
              </g>
            </defs>

            {/* Gear 1 · Chat */}
            <g className="gear-part" data-gear="1" transform="translate(130 265) scale(1.55)" style={{ "color": "#F0A46A" }}>
              <g className="gear-rotor gear-chat"><use href="#gear-form" /></g>
              <text className="gear-number" x="0" y="0" dy="-.08em" fill="#1F2A44"
                    style={{ "fontSize": "24px" }}>1</text>
            </g>

            {/* Gear 2 · Assistant */}
            <g className="gear-part" data-gear="2" transform="translate(274 178) scale(1.275)" style={{ "color": "#EBCB55" }}>
              <g className="gear-rotor gear-assistant"><use href="#gear-form" /></g>
              <text className="gear-number" x="0" y="0" dy="-.08em" fill="#1F2A44"
                    style={{ "fontSize": "25px" }}>2</text>
            </g>

            {/* Gear 3 · Agents */}
            <g className="gear-part" data-gear="3" transform="translate(393 103) scale(1.05)" style={{ "color": "#5FC6D8" }}>
              <g className="gear-rotor gear-agents"><use href="#gear-form" /></g>
              <text className="gear-number" x="0" y="0" dy="-.08em" fill="#1F2A44"
                    style={{ "fontSize": "27px" }}>3</text>
            </g>

            {/* Gear 4 · Loops */}
            <g className="gear-part" data-gear="4" transform="translate(487 42) scale(.85)" style={{ "color": "#77C58A" }}>
              <g className="gear-rotor gear-loops"><use href="#gear-form" /></g>
              <text className="gear-number" x="0" y="0" dy="-.08em" fill="#1F2A44"
                    style={{ "fontSize": "29px" }}>4</text>
            </g>

            {/* Problem landscape: one concise terrain statement for each gear. */}
            <g fill="none" strokeWidth="2.5">
              <path className="gear-part" data-gear="4" d="M535 42 L565 54 L600 54" stroke="#77C58A" />
              <path className="gear-part" data-gear="3" d="M454 103 L550 119 L600 119" stroke="#5FC6D8" />
              <path className="gear-part" data-gear="2" d="M348 178 L550 184 L600 184" stroke="#EBCB55" />
              <path className="gear-part" data-gear="1" d="M220 265 L550 249 L600 249" stroke="#F0A46A" />
            </g>

            <g style={{ "fontFamily": "inherit" }}>
              <g className="gear-part gear-copy" data-gear="4" aria-label="Loops. Recurring outcome · continuous system.">
                <text x="620" y="49" fill="#047857" style={{ "fontSize": "36px", "fontWeight": "800" }}>Loops</text>
                <text x="620" y="78" fill="#6B7482" style={{ "fontSize": "24px", "fontWeight": "650" }}>Sustain an outcome</text>
              </g>

              <g className="gear-part gear-copy" data-gear="3" aria-label="Agents. Multi-step objective · delegated run.">
                <text x="620" y="114" fill="#0E7490" style={{ "fontSize": "36px", "fontWeight": "800" }}>Agents</text>
                <text x="620" y="143" fill="#6B7482" style={{ "fontSize": "24px", "fontWeight": "650" }}>Complete an objective</text>
              </g>

              <g className="gear-part gear-copy" data-gear="2" aria-label="Assistant. One finished deliverable · sustained help.">
                <text x="620" y="179" fill="#A16207" style={{ "fontSize": "36px", "fontWeight": "800" }}>Assistant</text>
                <text x="620" y="208" fill="#6B7482" style={{ "fontSize": "24px", "fontWeight": "650" }}>Finish a deliverable</text>
              </g>

              <g className="gear-part gear-copy" data-gear="1" aria-label="Chat. One question or idea · quick burst.">
                <text x="620" y="244" fill="#B45309" style={{ "fontSize": "36px", "fontWeight": "800" }}>Chat</text>
                <text x="620" y="273" fill="#6B7482" style={{ "fontSize": "24px", "fontWeight": "650" }}>Answer a question</text>
              </g>
            </g>

          </svg>

          <div className="gear-mobile">
            <svg className="gear-mobile-train" viewBox="0 20 500 330" role="img"
                 aria-label="Four interlocking AI gears resting on a foundation of human intelligence, becoming smaller and faster from Chat through Assistant and Agents to Loops."
                 preserveAspectRatio="xMidYMid meet">
              <defs>
                <rect id="gear-tooth-mobile" x="-7" y="-58" width="14" height="17" rx="2" />
                <g id="gear-form-mobile">
                  <circle r="46" fill="currentColor" fillOpacity=".85" stroke="currentColor" strokeWidth="2.4" />
                  <g fill="currentColor" fillOpacity=".95" stroke="currentColor" strokeWidth="1.4">
                    <use href="#gear-tooth-mobile" />
                    <use href="#gear-tooth-mobile" transform="rotate(30)" />
                    <use href="#gear-tooth-mobile" transform="rotate(60)" />
                    <use href="#gear-tooth-mobile" transform="rotate(90)" />
                    <use href="#gear-tooth-mobile" transform="rotate(120)" />
                    <use href="#gear-tooth-mobile" transform="rotate(150)" />
                    <use href="#gear-tooth-mobile" transform="rotate(180)" />
                    <use href="#gear-tooth-mobile" transform="rotate(210)" />
                    <use href="#gear-tooth-mobile" transform="rotate(240)" />
                    <use href="#gear-tooth-mobile" transform="rotate(270)" />
                    <use href="#gear-tooth-mobile" transform="rotate(300)" />
                    <use href="#gear-tooth-mobile" transform="rotate(330)" />
                  </g>
                  <circle r="19" fill="#fff" stroke="currentColor" strokeWidth="2" />
                </g>
              </defs>

              <g transform="translate(115 262) scale(1.2)" style={{ "color": "#F0A46A" }}>
                <g className="gear-rotor gear-chat"><use href="#gear-form-mobile" /></g>
                <text className="gear-number" x="0" y="0" dy="-.08em" fill="#1F2A44" style={{ "fontSize": "24px" }}>1</text>
              </g>
              <g transform="translate(224 194) scale(1)" style={{ "color": "#EBCB55" }}>
                <g className="gear-rotor gear-assistant"><use href="#gear-form-mobile" /></g>
                <text className="gear-number" x="0" y="0" dy="-.08em" fill="#1F2A44" style={{ "fontSize": "25px" }}>2</text>
              </g>
              <g transform="translate(316 134) scale(.84)" style={{ "color": "#5FC6D8" }}>
                <g className="gear-rotor gear-agents"><use href="#gear-form-mobile" /></g>
                <text className="gear-number" x="0" y="0" dy="-.08em" fill="#1F2A44" style={{ "fontSize": "27px" }}>3</text>
              </g>
              <g transform="translate(388 82) scale(.68)" style={{ "color": "#77C58A" }}>
                <g className="gear-rotor gear-loops"><use href="#gear-form-mobile" /></g>
                <text className="gear-number" x="0" y="0" dy="-.08em" fill="#1F2A44" style={{ "fontSize": "29px" }}>4</text>
              </g>
            </svg>

            <ol className="gear-mobile-list">
              <li style={{ "--ga": "#F0A46A", "--gi": "#B45309", "--gt": "#FCEBDE" } as CSSProperties}><strong>1 · Chat</strong><span>Answer a question</span></li>
              <li style={{ "--ga": "#EBCB55", "--gi": "#A16207", "--gt": "#FCF4DA" } as CSSProperties}><strong>2 · Assistant</strong><span>Finish a deliverable</span></li>
              <li style={{ "--ga": "#5FC6D8", "--gi": "#0E7490", "--gt": "#DCF3F6" } as CSSProperties}><strong>3 · Agents</strong><span>Complete an objective</span></li>
              <li style={{ "--ga": "#77C58A", "--gi": "#047857", "--gt": "#E1F2E5" } as CSSProperties}><strong>4 · Loops</strong><span>Sustain an outcome</span></li>
            </ol>
          </div>
      </div>

      <div className="gear-foundation" data-foundation="">
        <div className="gear-foundation-title">
          <span>Foundation</span>
          <strong>Human Intelligence</strong>
        </div>
        <p>Sets direction · defines good · reviews the work · owns the outcome</p>
      </div>
    </section>
  </div>
);

export default AIUsageGears;
